// src/components/eventos/ModalEvento.js
import React, { useState, useEffect } from 'react';
import { X, Save } from 'lucide-react';
import { eventosEmergenciaAPI } from '../../services/apiService';

const ModalEvento = ({ isOpen, onClose, evento = null, onSuccess }) => {
  const isEdit = evento !== null;
  
  const [formData, setFormData] = useState({
    Fenomeno: '',
    Tipo: '',
    Año: new Date().getFullYear(),
    FechaInicio: '',
    FechaFin: ''
  });

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (isOpen && evento) {
      setFormData({
        Fenomeno: evento.Fenomeno || '',
        Tipo: evento.Tipo || '',
        Año: evento.Año || new Date().getFullYear(),
        FechaInicio: evento.FechaInicio?.split('T')[0] || '',
        FechaFin: evento.FechaFin?.split('T')[0] || ''
      });
    } else if (isOpen && !evento) {
      // Reset para nuevo
      setFormData({
        Fenomeno: '',
        Tipo: '',
        Año: new Date().getFullYear(),
        FechaInicio: '',
        FechaFin: ''
      });
    }
    setError(null);
  }, [isOpen, evento]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError(null);

    try {
      if (isEdit) {
        await eventosEmergenciaAPI.update(evento.Id, formData);
      } else {
        await eventosEmergenciaAPI.create(formData);
      }

      onSuccess();
      onClose();
    } catch (err) {
      console.error('Error al guardar:', err);
      setError(err.response?.data?.message || 'Error al guardar el evento');
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black/50 z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="sticky top-0 bg-white border-b border-gray-200 p-6 flex items-center justify-between z-10">
          <div>
            <h2 className="text-2xl font-bold text-gray-900">
              {isEdit ? 'Editar Evento' : 'Nuevo Evento'}
            </h2>
            <p className="text-sm text-gray-500 mt-1">
              {isEdit ? `ID: ${evento.Id}` : 'Registre un nuevo fenómeno o evento'}
            </p>
          </div>
          <button
            onClick={onClose}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <X className="w-6 h-6 text-gray-600" />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} className="p-6">
          {error && (
            <div className="mb-6 p-4 bg-red-50 border border-red-200 rounded-lg text-red-700">
              {error}
            </div>
          )}

          <div className="space-y-6">
            {/* Fenómeno */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Fenómeno / Nombre del Evento *
              </label>
              <input
                type="text"
                name="Fenomeno"
                value={formData.Fenomeno}
                onChange={handleChange}
                required
                placeholder="Ej: Huracán Mitch, Tormenta Tropical Eta"
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
              />
              <p className="text-xs text-gray-500 mt-1">
                Nombre específico del fenómeno o evento
              </p>
            </div>

            {/* Tipo */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Tipo de Evento *
              </label>
              <select
                name="Tipo"
                value={formData.Tipo}
                onChange={handleChange}
                required
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
              >
                <option value="">Seleccionar...</option>
                <option value="Huracán">Huracán</option>
                <option value="Tormenta Tropical">Tormenta Tropical</option>
                <option value="Inundación">Inundación</option>
                <option value="Deslizamiento">Deslizamiento</option>
                <option value="Sequía">Sequía</option>
                <option value="Incendio">Incendio</option>
                <option value="Terremoto">Terremoto</option>
                <option value="Otro">Otro</option>
              </select>
            </div>

            {/* Año */}
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Año *
              </label>
              <input
                type="number"
                name="Año"
                value={formData.Año}
                onChange={handleChange}
                required
                min="1900"
                max={new Date().getFullYear() + 1}
                className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
              />
            </div>

            {/* Fechas */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Inicio
                </label>
                <input
                  type="date"
                  name="FechaInicio"
                  value={formData.FechaInicio}
                  onChange={handleChange}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
                />
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  Fecha de Fin
                </label>
                <input
                  type="date"
                  name="FechaFin"
                  value={formData.FechaFin}
                  onChange={handleChange}
                  min={formData.FechaInicio}
                  className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#c8a88c] focus:border-transparent"
                />
              </div>
            </div>
          </div>

          {/* Footer */}
          <div className="flex justify-end gap-3 mt-8 pt-6 border-t border-gray-200">
            <button
              type="button"
              onClick={onClose}
              className="px-6 py-2 border border-gray-300 text-gray-700 rounded-lg font-medium hover:bg-gray-50 transition-colors"
            >
              Cancelar
            </button>
            <button
              type="submit"
              disabled={loading}
              className="flex items-center gap-2 px-6 py-2 bg-[#c8a88c] text-white rounded-lg font-medium hover:bg-[#b8987c] disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
            >
              {loading ? (
                <>
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white"></div>
                  Guardando...
                </>
              ) : (
                <>
                  <Save className="w-5 h-5" />
                  {isEdit ? 'Actualizar' : 'Guardar'}
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default ModalEvento;